import { RematchChallenge, UserDailyGame } from "@chesshotel/types";
import { createReduxModule } from "hooks-for-redux";
import { generateGameHistory } from "../modules/daily/cGamePlayActions";
import { VariantTypes } from "@chesshotel/constants";

export interface DailyInvite {
  id: number;
  username: string;
  userId: number;
  variantId: VariantTypes;
  turnLength: number;
  elo: number;
  blink?: boolean;
  leaguePlay: boolean;
}

interface DailyState {
  games: any;
  invites: DailyInvite[];
  opponentStatus: { online: boolean; userId: number | null };
}

const initialState: DailyState = {
  games: {},
  invites: [],
  opponentStatus: { online: false, userId: null },
};

export const [
  useDaily,
  {
    setGame,
    addDailyInvite,
    removeDailyInvite,
    setTime,
    setUserDailyGames,
    setDailyInvites,
    setChat,
    setMoves,
    setPositionIndex,
    setWSNewMove,
    setOpponentStatus,
    setBlinkingDaily,
    setRematch,
    reset,
  },
  dailyStore,
] = createReduxModule("daily", initialState, {
  setGame: (state, game) => ({
    ...state,
    games: {
      ...state.games,
      [game.id]: game,
    },
  }),
  addDailyInvite: (state, invite) => {
    if (state.invites.some((inv) => inv.id === invite.id)) return state;
    const newInvites = state.invites?.concat(invite);
    return {
      ...state,
      invites: newInvites,
    };
  },
  removeDailyInvite: (state, id) => {
    return {
      ...state,
      invites: state.invites?.filter((inv) => inv.id !== id),
    };
  },
  setRematch: (state, rematchChallenge: RematchChallenge) => {
    const gameId = rematchChallenge.oldGameId;

    return {
      ...state,
      games: {
        ...state.games,
        [gameId]: {
          ...state.games[gameId],
          rematchChallenge,
        },
      },
    };
  },

  setTime: (state, { gameId, time }) => ({
    ...state,
    games: {
      ...state.games,
      [gameId]: { ...state.games[gameId], time },
    },
  }),
  setUserDailyGames: (state, games) => {
    return {
      ...state,
      games,
    };
  },
  setDailyInvites: (state, invites) => ({
    ...state,
    invites,
  }),
  setChat: (state, { gameId, chatMessage }) => ({
    ...state,
    games: {
      ...state.games,
      [gameId]: {
        ...state.games[gameId],
        chatMessages: [...state.games[gameId].chatMessages, chatMessage],
      },
    },
  }),
  setMoves: (state, { gameId, pgn }) => ({
    ...state,
    games: {
      ...state.games,
      [gameId]: {
        ...state.games[gameId],
        pgn: [...state.games[gameId].pgn, pgn],
      },
    },
  }),
  setWSNewMove: (
    state,
    { gameId, move, possibleMoves, fen, lastMoveTime, san }
  ) => {
    const newPosition = state.games[gameId]?.positions;

    if (!newPosition) {
      return state;
    }

    newPosition.push(fen);
    const pgn = state.games[gameId]?.pgn;
    const newPgn = pgn ? pgn.concat(" ", san) : san;
    return {
      ...state,
      games: {
        ...state.games,
        [gameId]: {
          ...state.games[gameId],
          moves: [...state.games[gameId].moves, move],
          positions: newPosition,
          fen,
          positionIndex: newPosition.length - 1,
          turn: true,
          possibleMoves,
          lastMoveTime,
          pgn: newPgn,
        },
      },
    };
  },
  setOpponentStatus: (state, { userId, online }) => ({
    ...state,
    opponentStatus: {
      userId,
      online,
    },
  }),
  setPositionIndex: (state, { gameId, positionIndex }) => ({
    ...state,
    games: {
      ...state.games,
      [gameId]: { ...state.games[gameId], positionIndex },
    },
  }),
  setBlinkingDaily: (state, { variant, turnLength, userId, blinking }) => {
    const challs = state.invites.map((chall) => {
      if (
        variant == chall.variantId &&
        turnLength == chall.turnLength &&
        userId == chall.userId
      ) {
        chall.blink = blinking;
      }
      return chall;
    });

    return {
      ...state,
      corrChalls: challs,
    };
  },
  reset: (state) => initialState,
});

export async function doSetDailyGames(games) {
  const gamesObject = {};
  const curGames = dailyStore.getState().games;
  games.forEach(async (game: UserDailyGame) => {
    const currentGame = curGames[game.id];
    game = await generateGameHistory(game);
    if (currentGame) {
      gamesObject[game.id] = { ...currentGame, ...game };
    } else {
      gamesObject[game.id] = game;
    }
  });
  setUserDailyGames(gamesObject);
}
